import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid
} from "recharts";
import { DollarSign, ShoppingCart, Users, AlertTriangle, Package } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "Components/ui/card";
import KPICard from "./KPICard";
import OrderStatusPanel from "./OrderStatusPanel";
import CustomerInsightsPanel from "./CustomerInsightsPanel";
import InventoryAlertsPanel from "./InventoryAlertsPanel";
import DateRangePicker from "./DateRangePicker";
import { getDashboardData } from "../../redux/dashboard/action";
import "./Dashboard.css";

const CHART_COLORS = ["#4B7A8E", "#2E7D5B", "#D4A017", "#5B8DEF", "#D14343", "#8A9BA8"];

export default function Dashboard() {
  const dispatch = useDispatch();
  const list = useSelector((state) => state.reusableSlice.DashboardData);
  const headerCount = useSelector((state) => state?.bookingSlice?.getHeaderCount);

  const [params, setParams] = useState({
    date_filter: "today",
    end_date: "",
    search_key: "",
    start_date: "",
  });

  useEffect(() => {
    dispatch(getDashboardData(params));
  }, [dispatch, params]);

  const handleDateRangeApply = (dateRange) => {
    setParams((prev) => ({ ...prev, ...dateRange }));
  };

  const salesData = list?.sales || {};
  const ordersData = list?.orders || {};
  const customersData = list?.customers || {};

  // Everything paid that has not left the building yet.
  const pendingOrders =
    (headerCount?.order_placed || 0) +
    (headerCount?.order_confirmed || 0) +
    (headerCount?.processing || 0);

  return (
    <div className="dashboard-unified p-4">
      {/* Header row */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ fontFamily: "Aenoik-medium", fontSize: "24px", color: "#25343F" }}>Dashboard</h2>
        <DateRangePicker onApply={handleDateRangeApply} />
      </div>

      {/* KPI Strip */}
      <div className="kpi-strip">
        <KPICard
          label="Today's Revenue"
          value={`SAR ${salesData.daily_sales || 0}`}
          icon={DollarSign}
        />
        <KPICard
          label="Total Orders"
          value={salesData.daily_order_count || 0}
          icon={ShoppingCart}
        />
        <KPICard
          label="New Customers"
          value={customersData.daily_new_customers || 0}
          icon={Users}
        />
        <KPICard
          label="Pending Orders"
          value={pendingOrders}
          icon={AlertTriangle}
        />
        <KPICard
          label="Cancelled"
          value={salesData.daily_cancelled_order_count || 0}
          icon={Package}
        />
      </div>

      {/* Main Content: Charts + Live Orders */}
      <div className="row g-4 mt-1">
        {/* Revenue & Sales — left */}
        <div className="col-lg-7">
          <Card className="mb-4">
            <CardHeader>
              <CardTitle style={{ fontSize: "16px" }}>Revenue — Last 15 Days</CardTitle>
              <p style={{ fontFamily: "inter-regular", fontSize: "13px", color: "#8A9BA8" }}>
                Total: SAR {salesData.sales_last_15_days?.total || 0}
              </p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={salesData.sales_last_15_days?.trend || []}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4B7A8E" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#4B7A8E" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAEFEF" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8A9BA8" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#8A9BA8" }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="total" stroke="#4B7A8E" strokeWidth={2} fill="url(#revenueGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Platform Sales */}
          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: "16px" }}>Sales by Platform</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={salesData.sales_static || []} barSize={20} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAEFEF" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8A9BA8" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#8A9BA8" }} />
                  <Tooltip />
                  <Bar dataKey="IOS" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Website" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Android" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Live Orders — right */}
        <div className="col-lg-5">
          <OrderStatusPanel />
        </div>
      </div>

      {/* Bottom Row: Customer Insights + Inventory */}
      <div className="row g-4 mt-1">
        <div className="col-lg-6">
          <CustomerInsightsPanel data={customersData} />
        </div>
        <div className="col-lg-6">
          <InventoryAlertsPanel data={null} />
        </div>
      </div>
    </div>
  );
}
